import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import colors from 'styles/colors';
import media from 'styles/media';

import CloseIcon from 'components/svg/CloseIcon';

import { utagCompareRemoveCruise } from 'contexts/AnalyticsContext/functions';

import { BrandCode } from 'types';

const CruiseTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 20px;

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      gap: 10px;
    `,
    [BrandCode.RoyalCaribbean]: `
      gap: 5px;
    `,
  })[theme.brandCode]}

  .comparing & {
    opacity: 0;
  }
`;

const CruiseName = styled.h2`
  text-align: right;
  font-family: ${({ theme }) => theme.typography.fontFamilySemiBold};
  color: ${({ theme }) => theme.palette.text.primary};
  line-height: 150% !important;

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      font-size: 18px;
      ${media.small`
        font-size: 13px;
      `}
    `,
    [BrandCode.RoyalCaribbean]: `
      font-size: 20px;
      ${media.small`
        font-size: 16px;
      `}
    `,
  })[theme.brandCode]}

  ${media.small`
    line-height: normal !important;
    ${({ theme }) => ({
      [BrandCode.Celebrity]: `
        font-size: 13px;
      `,
      [BrandCode.RoyalCaribbean]: `
        font-size: 16px;
      `,
    })[theme.brandCode]}
  `}
`;

const RemoveCruiseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      svg {
        width: 16px;
        height: 16px;

        path {
          stroke: ${theme.palette.text.secondary};
          stroke-width: 1px;
        }
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      svg {
        width: 12px;
        height: 12px;
      }
      padding: 6px;
    `,
  })[theme.brandCode]}

  & > svg {
    padding: 0;
  }

  path {
    transition: stroke 0.2s cubic-bezier(0, 0, 0, 0);
    width: 1;
  }

  &:disabled {
    opacity: 0.2;
    pointer-events: none;
  }
`;

export const COMPARED_CRUISE_TITLE_CLASS = 'compared-cruise-title';

/**
 * Used by CompareCruisesList and in ComparePageHeader
 * Displays the cruise name next to an 'X' button that will remove the cruise from the comparing list.
 *
 * @param {object} cruise - Data recieved from the graph.
 * @param {func} remove - Function to remove cruise from the list.
 * @param {bool} removeDisabled - On the Compare page, if only 2 cruises are being compared, parent will tell this component that the button should be disabled.
 */

function ComparedCruiseTitle({ cruise, remove, removeDisabled }) {
  const router = useRouter();
  const cruiseName = cruise?.masterSailing?.itinerary?.name;

  const onClickEvent = useCallback(() => {
    remove();
    utagCompareRemoveCruise(cruise, router.route);
  }, [cruise]);

  return (
    <CruiseTitleWrapper className={COMPARED_CRUISE_TITLE_CLASS}>
      <CruiseName>{cruiseName}</CruiseName>
      <RemoveCruiseButton onClick={onClickEvent} disabled={removeDisabled}>
        <CloseIcon
          color={colors.secondary.deepCove}
          size={16}
          className="compareHeader"
        />
      </RemoveCruiseButton>
    </CruiseTitleWrapper>
  );
}

ComparedCruiseTitle.propTypes = {
  cruise: PropTypes.object,
  remove: PropTypes.func.isRequired,
  removeDisabled: PropTypes.bool,
};

export default ComparedCruiseTitle;
